import React, { Component, forwardRef } from "react";
import { Formik, Form } from "formik";
import { connect } from "react-redux";
import { register, resetRegister } from "./actions";
import { retrieveRegisterData } from "./selectors";
import {
  Input,
  Button,
  Select,
  withStyles,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Link,
  Grid
} from "@material-ui/core";
import * as Yup from "yup";
import AuthLayout from "./AuthLayout";
import Recaptcha from "react-recaptcha";
import DocumentTitle from "react-document-title";
import countries from "assets/countries.json";
import Autocomplete from "../components/Autocomplete";
import { Link as RouterLink, Redirect } from "react-router-dom";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import IconButton from "@material-ui/core/IconButton";
import omit from "lodash.omit";
import GenericApi from "Api";
import ConfirmDialogAction from "BaseApp/actions/ConfirmDialog";
import ConfirmDialog from "BaseApp/ConfirmDialog/ConfirmDialog";
import Spinner from "components/Spinner/Spinner";
import { findKey } from "lodash";
import { downloadPDF } from "./utils";

const requiredFieldText = "This field is required";

const registerSchema = Yup.object().shape({
  title: Yup.string().required(requiredFieldText),
  username: Yup.string()
    .required(requiredFieldText)
    .matches("^[A-Za-z][A-Za-z0-9]*$", "Invalid username"),
  password: Yup.string()
    .required(requiredFieldText)
    .min(15, "Must have at least 15 characters")
    .max(100, "Must have at most 100 characters")
    .matches(
      /(?=.*[!@#$%^&*(),.?":{}|<>])/,
      'Must include at least one special character: !@#$%^&*(),.?":{}|<>'
    ),
  passwordRepeat: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords do not match")
    .required(requiredFieldText),
  firstName: Yup.string().required(requiredFieldText),
  lastName: Yup.string().required(requiredFieldText),
  email: Yup.string()
    .email("Please enter a valid e-mail address")
    .required(requiredFieldText),
  emailRepeat: Yup.string()
    .oneOf([Yup.ref("email"), null], "Emails do not match")
    .required(requiredFieldText),
  company: Yup.string().required(requiredFieldText),
  jobTitle: Yup.string().required(requiredFieldText),
  streetName: Yup.string().required(requiredFieldText),
  streetNumber: Yup.string().required(requiredFieldText),
  zipCode: Yup.string().required(requiredFieldText),
  city: Yup.string().required(requiredFieldText),
  country: Yup.string().required(requiredFieldText),
  phone: Yup.string()
    .required(requiredFieldText)
    .matches(/^[\d+]+$/, "Phone number can only contain digits and +"),
  reference: Yup.string().required(requiredFieldText),
  reference_other: Yup.string().when("reference", {
    is: "other",
    then: Yup.string()
      .required(requiredFieldText)
      .max(500, "Must be 500 characters or less"),
    otherwise: Yup.string().notRequired()
  }),
  structure_design: Yup.string().required(requiredFieldText),
  polarization_dependent: Yup.string().required(requiredFieldText),
  wavelengths: Yup.string().required(requiredFieldText),
  better_tool: Yup.string().required(requiredFieldText),
  hosting_type: Yup.string().required(requiredFieldText)
});

const mapState = state => ({
  registerData: retrieveRegisterData(state)
});

const mapDispatch = dispatch => ({
  register: formData => dispatch(register(formData)),
  resetRegister: () => dispatch(resetRegister()),
  showConfirmDialog: (
    title,
    message,
    confirmAction,
    cancelAction,
    isReduxAction,
    postConfirm,
    inputPlaceholders,
    uploadFile,
    confirmLabels
  ) =>
    dispatch(
      ConfirmDialogAction.show(
        title,
        message,
        confirmAction,
        cancelAction,
        isReduxAction,
        postConfirm,
        inputPlaceholders,
        uploadFile,
        confirmLabels
      )
    )
});

const styles = {
  field: {
    marginTop: "5px",
    width: "100%"
  },
  validationMessage: {
    height: "25px",
    padding: "3px 0",
    color: "red"
  },
  recaptcha: {
    margin: "10px 0"
  },
  select: {
    width: "100%"
  },
  bottomText: {
    fontWeight: 500,
    marginTop: 5
  },
  bottom: {
    marginTop: 40,
    textAlign: "center"
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
};

const referenceOptions = {
  photonics_media: "Webinar - Photonics Media",
  photonics_west: "Conference - SPIE Photonics West",
  photonics_europe: "Conference - SPIE Photonics Europe",
  optica: "Conference - Optica",
  epic: "Conference - EPIC",
  cioe: "Conference - CIOE (China International Optoeletronic Expo)",
  spie_optical_systems_design: "Conference - SPIE Optical Systems Design",
  youtube: "YouTube",
  bilibili: "Bilibili",
  linkedin_post: "LinkedIn post by PlanOpSim",
  colleague: "Colleague",
  other: "Other"
};

const AdapterLink = forwardRef((props, ref) => (
  <RouterLink innerRef={ref} {...props} />
));

/**
 * A component to allow the users to register. When the registration
 * is complete, the user should be redirected to RegisterComplete component
 */
export class Register extends Component {
  countrySuggestions = [];

  constructor(props) {
    super(props);

    this.state = {
      recaptchaVerified: false,
      recaptchaLoaded: false,
      recaptchaExpired: false,
      showPassword: false,
      showPasswordRepeat: false,
      agreementPDFfile: null,
      agreementPDFBlobUrl: null
    };
  }

  componentDidMount() {
    this.countrySuggestions = countries.map(val => ({
      label: val.country,
      value: val.country
    }));
  }

  componentWillUnmount = () => {
    const { resetRegister } = this.props;
    resetRegister();
  };

  recaptchaOnloadCallback = () => {
    this.setState({ recaptchaLoaded: true });
  };

  recaptchaVerifyCallback = () => {
    this.setState({ recaptchaVerified: true });
    this.setState({ recaptchaExpired: false });
  };

  recaptchaOnExpiredCallback = () => {
    this.setState({ recaptchaExpired: true });
  };

  togglePasswordVisibility = () => {
    const { showPassword } = this.state;
    this.setState({ showPassword: !showPassword });
  };

  togglePasswordRepeatVisibility = () => {
    const { showPasswordRepeat } = this.state;
    this.setState({ showPasswordRepeat: !showPasswordRepeat });
  };

  handleSubmit = async data => {
    const { recaptchaLoaded, recaptchaVerified, recaptchaExpired } = this.state;
    const { register, registerData, showConfirmDialog } = this.props;
    // If "Other" is selected, use the value from reference_other
    const referenceValue =
      data.reference === "other"
        ? data.reference_other
        : referenceOptions[data.reference];
    const formData = {
      ...data,
      reference: referenceValue
    };
    if (registerData && registerData.loading) {
      return;
    } else if (
      process.env.REACT_APP_DEVELOPMENT === "true" ||
      (recaptchaLoaded && recaptchaVerified && !recaptchaExpired)
    ) {
      try {
        // first generate the pdf and only then create the user
        const response = await fetch(
          `${GenericApi.getBaseUrl()}/user/generate_agreement/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(
              omit(formData, [
                "passwordRepeat",
                "emailRepeat",
                "reference_other"
              ])
            )
          }
        );

        if (!response.ok) {
          const errors = await response.json();
          console.error("PDF generation failed:", errors);
          return;
        }

        const responseBlob = await response.blob();
        const agreementPDFfile = new File([responseBlob], "agreement.pdf");
        const agreementPDFBlobUrl = URL.createObjectURL(responseBlob);
        this.setState(
          {
            agreementPDFfile: agreementPDFfile,
            agreementPDFBlobUrl: agreementPDFBlobUrl
          },
          () => {
            const registerUser = receive_newsletter => {
              register(
                omit(
                  {
                    ...formData,
                    receive_newsletter,
                    agreement: this.state.agreementPDFfile,
                    has_accepted_current_eula: true
                  },
                  ["passwordRepeat", "emailRepeat", "reference_other"]
                )
              );
            };
            showConfirmDialog(
              "License Agreement",
              [
                <div>
                  <iframe
                    test-data="document_frame"
                    src={this.state.agreementPDFBlobUrl}
                    frameborder="1"
                    scrolling="auto"
                    height="100%"
                    width="100%"
                  ></iframe>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      test-data="downloadAgreementButton"
                      onClick={() =>
                        downloadPDF(this.state.agreementPDFBlobUrl)
                      }
                    >
                      Download Agreement
                    </Button>
                  </div>
                </div>
              ],
              [
                [
                  {
                    option:
                      "I want to receive newsletter emails from PlanOpSim",
                    actions: [
                      (resolve, reject) => {
                        registerUser(true);
                        resolve();
                      }
                    ]
                  },
                  {
                    option:
                      "I don't want to receive newsletter emails from PlanOpSim",
                    actions: [
                      (resolve, reject) => {
                        registerUser(false);
                        resolve();
                      }
                    ]
                  }
                ]
              ],
              () => {},
              false,
              () => {},
              false,
              false,
              ["agree", "disagree"]
            );
          }
        );
      } catch (error) {
        console.error("Error generating PDF:", error);
      }
    }
  };

  render = () => {
    const { registerData, classes } = this.props;
    const {
      showPassword,
      showPasswordRepeat,
      recaptchaVerified,
      recaptchaExpired
    } = this.state;
    const { error } = registerData || {};
    const { data } = registerData || {};
    const usernameError = data && data.username;
    const passwordError = data && data.password;
    const emailError = data && data.email;
    return registerData && registerData.success ? (
      <Redirect to="/register-complete" />
    ) : (
      <>
        <DocumentTitle title="Register">
          <AuthLayout title="Register">
            <div
              className={classes.validationMessage}
              style={{ textAlign: "center" }}
            >
              {error && usernameError && passwordError}
              {error &&
                !usernameError &&
                !passwordError &&
                !emailError &&
                "Something went wrong"}
            </div>

            <Formik
              initialValues={{
                username: "",
                password: "",
                passwordRepeat: "",
                title: "",
                firstName: "",
                lastName: "",
                email: "",
                emailRepeat: "",
                company: "",
                jobTitle: "",
                streetName: "",
                streetNumber: "",
                zipCode: "",
                city: "",
                country: "",
                phone: "",
                reference: "",
                reference_other: "",
                structure_design: "",
                polarization_dependent: "",
                wavelengths: "",
                better_tool: "",
                hosting_type: "cloud"
              }}
              validationSchema={registerSchema}
              onSubmit={this.handleSubmit}
            >
              {({
                errors,
                values,
                handleChange,
                handleBlur,
                touched,
                setFieldValue
              }) => (
                <Form>
                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="username"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.username}
                        placeholder="Username *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.username && errors.username}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        placeholder="Password *"
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={this.togglePasswordVisibility}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <div className={classes.validationMessage}>
                        {touched.password && errors.password}
                      </div>
                    </Grid>

                    <Grid item xs={6} style={{ paddingLeft: 10 }}>
                      <Input
                        className={classes.field}
                        type={showPasswordRepeat ? "text" : "password"}
                        name="passwordRepeat"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.passwordRepeat}
                        placeholder="Repeat Password *"
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password repeat visibility"
                              onClick={this.togglePasswordRepeatVisibility}
                            >
                              {showPasswordRepeat ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      <div className={classes.validationMessage}>
                        {touched.passwordRepeat && errors.passwordRepeat}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="firstName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.firstName}
                        placeholder="First name *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.firstName && errors.firstName}
                      </div>
                    </Grid>

                    <Grid item xs={6} style={{ paddingLeft: 10 }}>
                      <Input
                        className={classes.field}
                        name="lastName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.lastName}
                        placeholder="Last name *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.lastName && errors.lastName}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <FormControl
                        className={classes.field}
                        style={{ marginTop: -11 }}
                      >
                        <InputLabel id="demo-simple-select-label">
                          Title *
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          test-data="title"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{
                            name: "title",
                            id: "title"
                          }}
                        >
                          <MenuItem value="Mr.">Mr.</MenuItem>
                          <MenuItem value="Ms.">Ms.</MenuItem>
                          <MenuItem value="Dr.">Dr.</MenuItem>
                          <MenuItem value="Prof.">Prof.</MenuItem>
                        </Select>
                      </FormControl>
                      <div className={classes.validationMessage}>
                        {touched.title && errors.title}
                      </div>
                    </Grid>

                    <Grid item xs={6} style={{ paddingLeft: 10 }}>
                      <Input
                        className={classes.field}
                        name="jobTitle"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.jobTitle}
                        placeholder="Job title"
                      />
                      <div className={classes.validationMessage}>
                        {touched.jobTitle && errors.jobTitle}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        placeholder="E-mail *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.email && errors.email}
                      </div>
                    </Grid>

                    <Grid item xs={6} style={{ paddingLeft: 10 }}>
                      <Input
                        className={classes.field}
                        name="emailRepeat"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.emailRepeat}
                        placeholder="Repeat E-mail *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.emailRepeat && errors.emailRepeat}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="phone"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone}
                        placeholder="Phone number *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.phone && errors.phone}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="company"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.company}
                        placeholder="Company/Institution *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.company && errors.company}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="streetName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.streetName}
                        placeholder="Street name *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.streetName && errors.streetName}
                      </div>
                    </Grid>

                    <Grid item xs={6} style={{ paddingLeft: 10 }}>
                      <Input
                        className={classes.field}
                        name="streetNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.streetNumber}
                        placeholder="Street number *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.streetNumber && errors.streetNumber}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Input
                        className={classes.field}
                        name="zipCode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.zipCode}
                        placeholder="Zip code *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.zipCode && errors.zipCode}
                      </div>
                    </Grid>

                    <Grid item xs={6} style={{ paddingLeft: 10 }}>
                      <Input
                        className={classes.field}
                        name="city"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.city}
                        placeholder="City *"
                      />
                      <div className={classes.validationMessage}>
                        {touched.city && errors.city}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={6} style={{ paddingRight: 10 }}>
                      <Autocomplete
                        id="country"
                        name="country"
                        placeholder="Country *"
                        options={this.countrySuggestions.map(({ label }) => ({
                          id: label,
                          name: label
                        }))}
                        value={values.country}
                        initialInputValue={""}
                        onChange={newValue =>
                          setFieldValue("country", newValue)
                        }
                        onBlur={handleBlur}
                      />
                      <div className={classes.validationMessage}>
                        {touched.country && errors.country}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={12} style={{ paddingRight: 10 }}>
                      <FormControl
                        className={classes.field}
                        style={{ marginTop: -11 }}
                      >
                        <InputLabel id="hosting_type-label">
                          Hosting *
                        </InputLabel>
                        <Select
                          labelId="hosting_type-select-label"
                          id="hosting_type"
                          test-data="hosting_type"
                          value={values.hosting_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{
                            name: "hosting_type",
                            id: "hosting_type"
                          }}
                        >
                          <MenuItem value="cloud">Cloud</MenuItem>
                          <MenuItem value="local">Local</MenuItem>
                        </Select>
                      </FormControl>
                      <div className={classes.validationMessage}>
                        {touched.hosting_type && errors.hosting_type}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={12} style={{ paddingRight: 10 }}>
                      <FormControl
                        className={classes.field}
                        style={{ marginTop: -11 }}
                      >
                        <InputLabel id="reference-label">
                          How did you hear about us? *
                        </InputLabel>
                        <Select
                          labelId="reference-select-label"
                          id="reference"
                          test-data="reference"
                          value={values.reference}
                          onChange={e => {
                            handleChange(e);
                            if (e.target.value !== "other") {
                              setFieldValue("reference_other", "");
                            }
                          }}
                          onBlur={handleBlur}
                          inputProps={{
                            name: "reference",
                            id: "reference"
                          }}
                        >
                          {Object.entries(referenceOptions).map(
                            ([key, value]) => (
                              <MenuItem key={key} value={key}>
                                {value}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                      <div className={classes.validationMessage}>
                        {touched.reference && errors.reference}
                      </div>
                    </Grid>
                  </Grid>

                  {/* Show text field if "Other" is selected */}
                  {values.reference === "other" && (
                    <Grid container>
                      <Grid item xs={12}>
                        <FormControl className={classes.field}>
                          <InputLabel htmlFor="reference_other">
                            Please specify *
                          </InputLabel>
                          <Input
                            id="reference_other"
                            name="reference_other"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.reference_other || ""}
                            inputProps={{ maxLength: 500 }}
                          />
                        </FormControl>
                        <div className={classes.validationMessage}>
                          {touched.reference_other && errors.reference_other}
                        </div>
                      </Grid>
                    </Grid>
                  )}

                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        label="What functional optical structure are you designing: collimating lens, focusing lens, beam-splitter, fan-out, diffuser, etc? *"
                        name="structure_design"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.structure_design}
                        multiline
                        rows={5}
                        fullWidth
                        InputProps={{
                          style: {
                            paddingTop: "18px"
                          }
                        }}
                      />
                      <div className={classes.validationMessage}>
                        {touched.structure_design && errors.structure_design}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        label="Is the functionality of the desired optical structures polarization dependent? *"
                        name="polarization_dependent"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.polarization_dependent}
                        multiline
                        rows={5}
                        fullWidth
                        InputProps={{
                          style: {
                            paddingTop: "18px"
                          }
                        }}
                      />
                      <div className={classes.validationMessage}>
                        {touched.polarization_dependent &&
                          errors.polarization_dependent}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        label="What are your wavelengths of interest? *"
                        name="wavelengths"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.wavelengths}
                        multiline
                        rows={5}
                        fullWidth
                        InputProps={{
                          style: {
                            paddingTop: "18px"
                          }
                        }}
                      />
                      <div className={classes.validationMessage}>
                        {touched.wavelengths && errors.wavelengths}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        label="What do you hope PlanOpSim will do better than other tools? *"
                        name="better_tool"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.better_tool}
                        multiline
                        rows={5}
                        fullWidth
                        InputProps={{
                          style: {
                            paddingTop: "18px"
                          }
                        }}
                      />
                      <div className={classes.validationMessage}>
                        {touched.better_tool && errors.better_tool}
                      </div>
                    </Grid>
                  </Grid>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                    className={classes.recaptcha}
                  >
                    <Recaptcha
                      sitekey="6LddXp4kAAAAAPPY4heeQzI15h1_b1pSyzZAhk5e"
                      render="explicit"
                      verifyCallback={this.recaptchaVerifyCallback}
                      onloadCallback={this.recaptchaOnloadCallback}
                      expiredCallback={this.recaptchaOnExpiredCallback}
                    />
                  </div>
                  <div
                    className={classes.validationMessage}
                    style={{ textAlign: "center" }}
                  >
                    {!recaptchaVerified && "Please verify recaptcha"}
                    {recaptchaExpired &&
                      "Your recatpcha has expired, please verify again"}
                  </div>
                  <div style={{ position: "relative" }}>
                    <Button
                      className={classes.field}
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={registerData && registerData.loading}
                      test-data="submitRegistration"
                      onClick={() => {
                        const invalidInput = findKey(
                          errors,
                          key => key !== undefined
                        );
                        window.scrollTo(
                          0,
                          window.pageYOffset +
                            document.getElementsByName(invalidInput)[0]
                        );
                      }}
                    >
                      Submit
                    </Button>
                    {registerData && registerData.loading && (
                      <Spinner
                        name="Waiting"
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                  <div className={classes.bottom}>
                    <Typography>Have an account ?</Typography>
                    <Link component={AdapterLink} to="/login">
                      <Typography className={classes.bottomText}>
                        LOGIN
                      </Typography>
                    </Link>
                  </div>
                </Form>
              )}
            </Formik>
          </AuthLayout>
        </DocumentTitle>
        <ConfirmDialog />
      </>
    );
  };
}

export default connect(mapState, mapDispatch)(withStyles(styles)(Register));
